import { FunctionComponent, useState } from 'react'
import styles from './apiClientInformation.module.scss'
import Title from '@/components/_custom/Title/title'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { ApiClient } from '@/types/ApiClient.ts'
import moment from 'moment/moment'
import ValidDeleteClientApiDialog from './Dialog/validDeleteClientApiDialog.tsx'
import EditApiClientInformationDialog from './Dialog/editApiClientInformationDialog.tsx'
import { GeneratingTokens } from '@mui/icons-material'
import RevokeTokenClientApiDialog from './Dialog/revokeTokenClientApiDialog.tsx'

interface ApiClientInformationProps {
    apiClient: ApiClient
    refetch: () => void
    backToList: () => void
}

const ApiClientInformation: FunctionComponent<ApiClientInformationProps> = ({
    apiClient,
    refetch,
    backToList
}: ApiClientInformationProps) => {
    const { t } = useTranslation()

    const [isEditApiClientInformationDialogOpen, setEditApiClientInformationDialogOpen] = useState<boolean>(false)
    const [isDeleteApiClientDialogOpen, setDeleteApiClientDialogOpen] = useState<boolean>(false)
    const [isRevokeTokenApiClientDialogOpen, setRevokeTokenApiClientDialogOpen] = useState<boolean>(false)

    return (
        <div className={styles.Container}>
            <div className={styles.Title}>
                <Title text={t('clientApiInformations')} />
            </div>
            <div className={styles.GroupInformations}>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('adminID')}
                        value={apiClient.id.toString()}
                        readOnly
                        format={'pascal'}
                        className={styles.UserInput}
                    />
                    <UserInput
                        label={t('clientId')}
                        value={apiClient.clientId}
                        readOnly
                        format={'pascal'}
                        className={styles.UserInput}
                    />
                </div>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('name')}
                        value={apiClient.name}
                        readOnly
                        format={'pascal'}
                        className={styles.UserInput}
                    />
                    <UserInput
                        label={t('lastUsed')}
                        value={
                            apiClient.lastUsed
                                ? moment(apiClient.lastUsed, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm')
                                : ''
                        }
                        readOnly
                        format={'pascal'}
                        className={styles.UserInput}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    {apiClient.tokenCount >= 1 && (
                        <>
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('revokeToken') + ' (' + apiClient.tokenCount + ')'}
                                customIcon={<GeneratingTokens />}
                                onClick={() => setRevokeTokenApiClientDialogOpen(true)}
                                className={styles.Button}
                            />
                            {isRevokeTokenApiClientDialogOpen && (
                                <RevokeTokenClientApiDialog
                                    open={isRevokeTokenApiClientDialogOpen}
                                    handleClose={() => setRevokeTokenApiClientDialogOpen(false)}
                                    id={apiClient.id}
                                    name={apiClient.name}
                                    refetch={refetch}
                                />
                            )}
                        </>
                    )}
                    <ButtonTextIcon
                        type={'edit'}
                        onClick={() => setEditApiClientInformationDialogOpen(true)}
                        className={styles.Button}
                    />
                    {isEditApiClientInformationDialogOpen && (
                        <EditApiClientInformationDialog
                            open={isEditApiClientInformationDialogOpen}
                            handleClose={() => setEditApiClientInformationDialogOpen(false)}
                            apiClient={apiClient}
                            refetch={refetch}
                        />
                    )}
                    <ButtonTextIcon
                        type={'delete'}
                        onClick={() => setDeleteApiClientDialogOpen(true)}
                        className={styles.Button}
                    />
                    {isDeleteApiClientDialogOpen && (
                        <ValidDeleteClientApiDialog
                            open={isDeleteApiClientDialogOpen}
                            handleClose={() => setDeleteApiClientDialogOpen(false)}
                            id={apiClient.id}
                            name={apiClient.name}
                            backToList={backToList}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default ApiClientInformation
